import { t } from '@lingui/core/macro';
import { useMemo, useState } from 'react';

import { useCompanyOptions } from '@/api/activity';
import type { OptionType } from '@/api/common';
import {
  encodeFilterFieldName,
  FilterField,
  InfiniteComboboxPickerFilter,
  InfiniteScrollCategoryFilter,
  useFilterForm,
} from '@/components/filters';
import type { FilterOperatorMulti } from '@/components/InstantSearch';
import { usePageFilterContext } from '@/components/page';
import type { FilterComponentProps } from '@/providers/TableState';

type Props = FilterComponentProps & {
  mode?: 'multiple';
};

const CompanyFilter = ({ name, label, infoPopover, compact = false, mode = 'multiple' }: Props) => {
  const form = useFilterForm();
  const {
    pageFilters: { startDate, endDate },
  } = usePageFilterContext();
  const [search, setSearch] = useState<string>('');
  const { companyOptions, error, hasMore, isLoading, isFetchingNextPage, fetchNextPage } =
    useCompanyOptions({
      search,
      pageSize: 50,
      startDate,
      endDate,
    });

  const companyWithDomainFallbackOptions = useMemo<OptionType[] | undefined>(() => {
    if (!companyOptions) {
      return undefined;
    }

    return companyOptions.map((company) => ({
      id: company.id,
      name: company?.name ? `${company.name} (${company.tld})` : company.tld,
    }));
  }, [companyOptions]);

  return (
    <form.AppField name={encodeFilterFieldName(name)}>
      {(field) => (
        <FilterField
          data-testid="company-filter"
          label={label}
          infoPopover={infoPopover}
          hideLabel={!compact}
        >
          {compact ? (
            <InfiniteComboboxPickerFilter
              value={field.state.value as FilterOperatorMulti}
              onChange={field.handleChange}
              placeholder={t`Select Companies...`}
              loadingPlaceholder={t`Loading Companies...`}
              options={companyWithDomainFallbackOptions}
              isLoading={isLoading}
              isLoadingMore={isFetchingNextPage}
              hasMore={hasMore}
              error={error}
              loadMore={fetchNextPage}
              notFoundContent={t`No companies were found...`}
              onSearch={setSearch}
            />
          ) : (
            <InfiniteScrollCategoryFilter
              value={field.state.value as FilterOperatorMulti}
              onChange={field.handleChange}
              selectionMode={mode}
              options={companyWithDomainFallbackOptions}
              isLoading={isLoading}
              isLoadingMore={isFetchingNextPage}
              hasMore={hasMore}
              error={error}
              loadMore={fetchNextPage}
              searchText={search}
              onSearch={setSearch}
            />
          )}
        </FilterField>
      )}
    </form.AppField>
  );
};

export default CompanyFilter;
